import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  listBusinesses,
  selectBusinesses,
  selectBusinessesError,
  selectBusinessesLoading,
  selectBusinessesMessage,
} from "../businessSlice";

const PAGE_SIZE = 10;

const BusinessListPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const businesses = useSelector(selectBusinesses);
  const isLoading = useSelector(selectBusinessesLoading);
  const isError = useSelector(selectBusinessesError);
  const message = useSelector(selectBusinessesMessage);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(listBusinesses());
  }, [dispatch]);

  const getName = (biz) => {
    return biz?.businessName || "-";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return businesses || [];
    return (businesses || []).filter((biz) =>
      (biz?.businessName || "").toLowerCase().includes(term)
    );
  }, [businesses, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const formatDate = (value) => {
    if (!value) return "-";
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString("tr-TR");
    }
    if (typeof value === "number") {
      return new Date(value).toLocaleString("tr-TR");
    }
    if (typeof value === "string") return value;
    return "-";
  };

  const renderRows = () => {
    if (isLoading && (!businesses || businesses.length === 0)) {
      return (
        <tr>
          <td colSpan="5">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!businesses || businesses.length === 0)) {
      return (
        <tr>
          <td colSpan="5" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!paged || paged.length === 0) {
      return (
        <tr>
          <td colSpan="5">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return paged.map((biz) => (
      <tr key={biz.id || biz.businessId}>
        <td>{biz?.businessId || biz?.id || "-"}</td>
        <td>{highlightMatch(getName(biz), search.trim())}</td>
        <td>{biz?.businessMail || "-"}</td>
        <td>{biz?.businessState ? t("admin_common.status_active") : t("admin_common.status_passive")}</td>
        <td>{formatDate(biz?.createdAt)}</td>
      </tr>
    ));
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">{t("admin_business_pages.list_title")}</h4>
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("admin_common.search_name_placeholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>{t("admin_common.business_id")}</th>
                    <th>{t("admin_common.business")}</th>
                    <th>{t("admin_common.email")}</th>
                    <th>{t("admin_common.status")}</th>
                    <th>{t("admin_common.created_at")}</th>
                  </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
              </table>
            </div>
            <div className="d-flex align-items-center justify-content-between mt-3">
              <span className="text-muted">
                {t("admin_common.total_records", { count: filtered.length })}
              </span>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {t("admin_common.prev")}
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled>
                  {currentPage} / {totalPages}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  {t("admin_common.next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessListPage;
